import React from "react";
import { cn } from "../../lib/utils";

type ColumnConfig = {
  title: React.ReactNode;
  span?: number;
  align?: "left" | "center" | "right";
  className?: string;
};

type FilterConfig = {
  span?: number;
  content: React.ReactNode;
  className?: string;
};

interface TableFilterLayoutProps {
  columns: ColumnConfig[];
  filters: FilterConfig[];
  /** Optional heading shown at the top of the table header block */
  heading?: React.ReactNode;
  className?: string;
  headerClassName?: string;
  filtersClassName?: string;
  innerClassName?: string;
  isExpanded?: boolean;
}

const getSpanClass = (span?: number) => {
  const safeSpan = Math.min(Math.max(span ?? 1, 1), 12);
  return `col-span-${safeSpan}`;
};

export function TableFilterLayout({
  columns,
  filters,
  heading,
  className,
  headerClassName,
  filtersClassName,
  innerClassName,
  isExpanded = true,
}: TableFilterLayoutProps) {
  return (
    <div
      className={cn(
        "bg-sidebar-hover border-t border-b border-border transition-all duration-300",
        className,
      )}
    >
      <div className={cn("px-6 py-2 space-y-1", innerClassName)}>
        {heading != null && (
          <div className="text-sm font-semibold text-gray-900 dark:text-white border-b border-border pb-2 mb-1">
            {heading}
          </div>
        )}
        <div
          className={cn(
            "grid grid-cols-12 gap-4 text-xs font-medium uppercase tracking-wider text-content-text-muted select-none cursor-default min-h-[2rem]",
            headerClassName,
          )}
        >
          {columns.map((column, index) => (
            <div
              key={`col-${index}`}
              className={cn(
                "break-words",
                getSpanClass(column.span),
                column.align === "center" && "text-center",
                column.align === "right" && "text-right",
                column.className,
              )}
            >
              {column.title}
            </div>
          ))}
        </div>

        <div
          className={cn(
            "grid grid-cols-12 gap-4 transition-all duration-300 ease-in-out",
            filtersClassName,
            isExpanded
              ? "max-h-[500px] opacity-100 mt-4"
              : "max-h-0 opacity-0 overflow-hidden mt-0",
          )}
        >
          {filters.map((filter, index) => (
            <div
              key={index}
              className={cn(getSpanClass(filter.span), filter.className)}
            >
              {filter.content}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
